import { useState } from "react";
import BaseModal from "./BaseModal";
import Icon1 from "@assets/icons/Icon";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { atelierApi } from "@services/api/atelier";
import { useAtelierStore } from "@store/atelierStore";
import { showNotification } from "context/notification.service";
import { describeJob, type ShrinkPlan } from "@utils/atelierShrink";

/**
 * « Le travail a diminué — on raccourcit le planning ? »
 *
 * Un bloc posé occupe un créneau de mécanicien : le raccourcir d'office reviendrait à
 * réécrire sa journée sans le prévenir. On montre donc précisément ce qui serait libéré,
 * et l'atelier décide. Refuser est une réponse valable : le mécanicien peut vouloir
 * garder sa marge.
 */
export default function AdjustAtelierJobsModal({
  plan,
  onClose,
}: {
  plan: ShrinkPlan;
  onClose: () => void;
}) {
  const [applying, setApplying] = useState(false);

  const apply = async () => {
    setApplying(true);
    try {
      for (const adjustment of plan.adjustments) {
        if (adjustment.kind === "remove") {
          await atelierApi.deleteJob(adjustment.id);
        } else {
          // `hours` (la charge de travail) et `durationHours` (la place prise sur la
          // grille) vont de pair sur un bloc posé : n'en bouger qu'un laisserait la
          // carte annoncer une durée que le créneau ne tient pas.
          await atelierApi.updateJob(adjustment.id, {
            durationHours: adjustment.durationHours,
            hours: adjustment.durationHours,
          });
        }
      }
      // Plus de rechargement complet : l'écoute temps réel de l'écran atelier reçoit
      // les jobs raccourcis (updated_at). Elle ne voit pas la suppression d'un job
      // qu'elle n'a jamais reçu : on retire donc ceux-là du store nous-mêmes. Écran
      // fermé (pas d'écoute), la semaine chargée est marquée périmée et le prochain
      // affichage la relira.
      const removed = new Set(plan.adjustments.filter((a) => a.kind === "remove").map((a) => a.id));
      const state = useAtelierStore.getState();
      useAtelierStore.setState({
        jobs: state.jobs.filter((j) => !removed.has(j.id)),
        ...(state._realtimeUnsubs.length === 0 ? { _loadedRange: undefined } : {}),
      });
      showNotification({ type: "success", message: "Planning atelier ajusté" });
      onClose();
    } catch (e) {
      console.error("Error adjusting atelier jobs:", e);
      showNotification({ type: "error", message: "Erreur lors de l'ajustement du planning" });
      setApplying(false);
    }
  };

  return (
    <BaseModal
      title="Le travail a diminué"
      startIcon={<Icon1 name="warning" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      <div className="p-4 flex flex-col gap-3">
        <p className="text-h3">
          L'atelier a <strong>{plan.placed}h</strong> posées pour cet ordre, qui n'en
          demande plus que <strong>{plan.needed}h</strong>.
        </p>

        <div className="flex flex-col gap-2 rounded-lg border border-neutral-200 p-3">
          {plan.adjustments.map((adjustment) => (
            <div key={adjustment.id} className="flex flex-row items-baseline justify-between gap-3">
              <span className="text-h4 text-neutral-600 truncate">{describeJob(adjustment.job)}</span>
              {adjustment.kind === "remove" ? (
                <span className="text-h4 font-medium text-red-600 flex-shrink-0">
                  {adjustment.job.durationHours}h → retiré
                </span>
              ) : (
                <span className="text-h4 font-medium text-neutral-700 flex-shrink-0">
                  {adjustment.job.durationHours}h → {adjustment.durationHours}h
                </span>
              )}
            </div>
          ))}
        </div>

        <span className="text-h4 text-neutral-400">
          Les blocs sont rognés par la fin, pour ne rien déplacer d'autre dans la journée.
          Vous pouvez aussi laisser le planning tel quel.
        </span>
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>Laisser tel quel</SecondaryButton>
        <Button
          variant="small"
          label={applying ? "Ajustement..." : `Libérer ${plan.excess}h`}
          onClick={apply}
          disabled={applying}
        />
      </div>
    </BaseModal>
  );
}
